import { useState, useCallback, useMemo } from "react";
import { usePostMessageHandler } from "hooks/PostMessage/Handler";
import { postMessageTo } from "hooks/PostMessage/Utils";
import { ArrowNavigationAction } from "hooks/PostMessage/Actions";
import queryString from "data/queryString";
import { get } from "data/constants";

const useArrowNavigationTransmitter = (
    source,
    target,
    targetData,
    setCurrentIndex,
    history
) =>
    usePostMessageHandler(
        useMemo(
            () => ({
                [ArrowNavigationAction.Ready]: (data) =>
                    postMessageTo(
                        ArrowNavigationAction.Load,
                        targetData,
                        target
                    ),
                [ArrowNavigationAction.Paginate]: (data) => {
                    if (source !== data.source) {
                        return;
                    }
                    const search = queryString.parse(window.location.search);
                    search[data.qsParam] = data.currentIndex;
                    setCurrentIndex(data.currentIndex);
                    const a = document.createElement("a"); // Use a tag for Location conversion
                    a.href = window.location;
                    // Remove basename from pathname to fix relative browsing
                    a.pathname = a.pathname.replace(
                        get(window.CONFIG, ["host", "basename"], ""),
                        ""
                    );
                    a.search = queryString.stringify(search);
                    history.push(a.pathname + a.search);
                },
            }),
            [source, target, targetData, setCurrentIndex, history]
        )
    );

const useArrowNavigationReceiver = ({
    handlePagination = null,
    handleDeletedWhileOpen = null,
    activeRowIndex: currentIndexFromProps = 0,
} = {}) => {
    const [postMessageProps, setPostMessageProps] = useState({
        rows: [],
        currentIndex: currentIndexFromProps,
    });

    const { rows, currentIndex, source, qsParam } = postMessageProps;
    const [minCount, setMinCount] = useState(0);
    const [maxCount, setMaxCount] = useState(rows.length);

    const getActiveRowItemUri = useCallback(
        (rowIndex) => {
            return rows?.[rowIndex] ?? undefined;
        },
        [rows]
    );

    const isNextDisabled = useMemo(
        (): boolean => currentIndex + 1 >= maxCount,
        [currentIndex, maxCount]
    );

    const isPrevDisabled = useMemo(
        (): boolean => currentIndex <= minCount,
        [currentIndex, minCount]
    );

    const getNext = useCallback(
        (index: number): number => {
            for (let i = index; i < maxCount; ++i) {
                /* istanbul ignore else */
                if (rows[i] != null) {
                    return i;
                }
            }
            return index - 1;
        },
        [rows, maxCount]
    );

    const getPrev = useCallback(
        (index: number): number => {
            for (let i = index; i >= minCount; --i) {
                /* istanbul ignore else */
                if (rows[i] != null) {
                    return i;
                }
            }
            return index + 1;
        },
        [rows, minCount]
    );

    const onPaginate = useCallback(
        (index, postMessage = false) => {
            const itemUri = rows[index];
            if (itemUri == null) {
                return (
                    rows.length > 0 &&
                    typeof handleDeletedWhileOpen === "function" &&
                    handleDeletedWhileOpen({
                        itemUri,
                        rowIndex: index,
                        isNext: currentIndex < index,
                        onSetActiveRowIndex: /* istanbul ignore next */ (
                            index,
                            isNext
                        ) => (isNext ? getNext(index) : getPrev(index)),
                        getActiveRowItemUri,
                        minCount,
                    })
                );
            }
            typeof handlePagination === "function" &&
                handlePagination(rows[index], index);
            postMessage &&
                postMessageTo(
                    ArrowNavigationAction.Paginate,
                    {
                        source,
                        qsParam,
                        currentIndex: index,
                    },
                    window.opener /* istanbul ignore next */ || window
                );
        },
        [
            handlePagination,
            handleDeletedWhileOpen,
            currentIndex,
            qsParam,
            source,
            rows,
            getActiveRowItemUri,
            minCount,
            getNext,
            getPrev,
        ]
    );

    const onPrev = useCallback(() => {
        /* istanbul ignore if */
        if (isPrevDisabled) return;
        const prevRowIndex = getPrev(currentIndex - 1);
        if (prevRowIndex === currentIndex) {
            /* istanbul ignore else */
            if (minCount === 0 && minCount !== prevRowIndex)
                setMinCount(prevRowIndex);
        } else {
            setPostMessageProps((prevProps) => ({
                ...prevProps,
                currentIndex: prevRowIndex,
            }));
        }
        onPaginate(prevRowIndex, true);
    }, [getPrev, minCount, currentIndex, isPrevDisabled, onPaginate]);

    const onNext = useCallback(() => {
        /* istanbul ignore if */
        if (isNextDisabled) return;
        const nextRowIndex = getNext(currentIndex + 1);
        if (nextRowIndex === currentIndex) {
            /* istanbul ignore else */
            if (maxCount === rows.length && maxCount !== nextRowIndex + 1)
                setMaxCount(nextRowIndex + 1);
        } else {
            setPostMessageProps((prevProps) => ({
                ...prevProps,
                currentIndex: nextRowIndex,
            }));
        }
        onPaginate(nextRowIndex, true);
    }, [
        getNext,
        maxCount,
        rows.length,
        currentIndex,
        isNextDisabled,
        onPaginate,
    ]);

    usePostMessageHandler(
        useMemo(
            () => ({
                [ArrowNavigationAction.Load]: (data) => {
                    /* istanbul ignore if */
                    if (source) {
                        return;
                    }
                    setPostMessageProps({
                        ...data,
                        rows: data.rows ?? [],
                        currentIndex: data.currentIndex ?? 0,
                    });
                    setMinCount(0);
                    setMaxCount(data.rows?.length ?? 0);
                },
                [ArrowNavigationAction.Reload]: (data) => {
                    /* istanbul ignore next */
                    if (data.source !== source && !data.newSource) {
                        return;
                    }
                    const rows = data.rows ?? [];
                    let currentIndex = data.currentIndex ?? 0;
                    if (currentIndex >= rows.length) {
                        currentIndex = rows.length - 1;
                    }
                    setPostMessageProps((prevProps) => {
                        /* istanbul ignore else */
                        if (
                            prevProps.currentIndex !== currentIndex ||
                            data.newSource
                        ) {
                            onPaginate(currentIndex);
                        }
                        return {
                            ...prevProps,
                            rows,
                            currentIndex,
                            source: data.newSource ?? prevProps.source,
                        };
                    });
                    setMinCount(0);
                    setMaxCount(data.rows?.length ?? 0);
                },
                [ArrowNavigationAction.SetIndex]: (data) => {
                    if (
                        data.source !== source ||
                        data.currentIndex === currentIndex
                    ) {
                        return;
                    }
                    onPaginate(data.currentIndex);
                    setPostMessageProps((prevProps) => ({
                        ...prevProps,
                        currentIndex: data.currentIndex,
                    }));
                },
            }),
            [source, onPaginate, currentIndex]
        )
    );

    return useMemo(
        () => ({
            hasMoreThanOne: rows.length > 1,
            activeItemUri: getActiveRowItemUri(currentIndex),
            onNext,
            onPrev,
            isNextDisabled,
            isPrevDisabled,
        }),
        [
            rows.length,
            currentIndex,
            onNext,
            onPrev,
            isNextDisabled,
            isPrevDisabled,
            getActiveRowItemUri,
        ]
    );
};

export { useArrowNavigationTransmitter, useArrowNavigationReceiver };
